import type { IQuestion } from "@/apis/exam_papers/types";
import { useSaveAnswers } from "@/apis/exam_progress/saveAnswers";
import { useExamContext } from "@/pages/my_exams/context/ExamContext";
import type { IAnswerState } from "../context/ExamContext";
import React, { useMemo, useState, useEffect, useCallback } from "react";
import "react-quill-new/dist/quill.snow.css";
import { useParams } from "react-router-dom";
import katex from "katex";
import "katex/dist/katex.min.css";
import { convertToFraction } from "@/utils/math";
import { BookmarkIcon } from "@heroicons/react/24/solid/index.js";
import { message } from "antd";
import HighlightableContent from "./HighlightableContent";

interface ExamQuestionPanelProps {
  question: IQuestion;
  originalQuestion: string;
  questions: IQuestion[];
  onSaveHighlight?: (
    contentType: "passage" | "question",
    newContent: string
  ) => void;
}

// 알파벳 라벨 생성 함수
const getChoiceLetter = (idx: number) => String.fromCharCode(65 + idx);

const ExamQuestionPanel: React.FC<ExamQuestionPanelProps> = ({
  question,
  questions,
  onSaveHighlight,
}) => {
  const { id } = useParams<{ id: string }>();
  const { answers, updateAnswer, excludeMode, setExcludeMode } =
    useExamContext();
  const saveAnswersMutation = useSaveAnswers();

  const questionNumber = useMemo(() => {
    return questions.findIndex((q) => q.id === question.id) + 1;
  }, [questions, question.id]);

  const selectedChoice = useMemo(() => {
    return answers[question?.id]?.selected_choice_id || null;
  }, [answers, question]);

  const excludedChoices = useMemo(() => {
    return answers[question?.id]?.excluded_choices || [];
  }, [answers, question]);

  const markForReview = useMemo(() => {
    return answers[question?.id]?.marked_for_review || false;
  }, [answers, question]);

  // Short answer input state
  const [shortAnswer, setShortAnswer] = useState(
    (answers[question?.id]?.selected_choice_id ?? "") + ""
  );

  const debounceTimeout = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );

  useEffect(() => {
    if (question.answer_type === "short_answer") {
      setShortAnswer((answers[question?.id]?.selected_choice_id ?? "") + "");
    }
  }, [question.id, question.answer_type, answers]);

  const saveAnswer = (updates: Partial<IAnswerState>) => {
    if (!id) return;

    // 로컬 상태 업데이트
    updateAnswer(question.id, updates);

    // API로 저장
    saveAnswersMutation.mutate(
      {
        id: id,
        data: {
          question_id: question.id,
          selected_answer:
            updates.selected_choice_id ||
            answers[question.id]?.selected_choice_id ||
            "",
          excluded_choices:
            updates.excluded_choices ||
            answers[question.id]?.excluded_choices ||
            [],
          marked_for_review:
            updates.marked_for_review ??
            answers[question.id]?.marked_for_review ??
            false,
        },
      },
      {
        onError: () => {
          message.error("Failed to save answer. Please try again.");
        },
      }
    );
  };

  const handleSelectChoice = (choiceId: string) => {
    saveAnswer({ selected_choice_id: choiceId });
  };

  const handleToggleMarkForReview = () => {
    const currentAnswer = answers[question.id];
    saveAnswer({ marked_for_review: !currentAnswer?.marked_for_review });
  };

  const handleExcludeChoice = (choiceId: string) => {
    const currentAnswer = answers[question.id];
    const currentExcluded = currentAnswer?.excluded_choices || [];
    const newExcluded = currentExcluded.includes(choiceId)
      ? currentExcluded.filter((id) => id !== choiceId)
      : [...currentExcluded, choiceId];

    saveAnswer({ excluded_choices: newExcluded });
  };

  // Exclude Mode 토글 핸들러
  const onToggleExcludeMode = () => {
    setExcludeMode(!excludeMode);
  };

  return (
    <div className="w-1/2 h-full flex flex-col overflow-auto p-8 bg-white">
      {/* Mark for Review 바 */}
      <div className="flex items-center bg-[#ededed] max-w-[650px] mx-auto w-full relative pr-2 cursor-pointer select-none h-8 min-h-[32px]">
        <div className="flex-1 flex items-center" role="button" tabIndex={0}>
          <div className="w-8 h-full flex items-center justify-center bg-black text-white font-bold text-base rounded-none mr-2">
            {questionNumber}
          </div>
          <div
            className={`flex items-center text-sm ${
              markForReview
                ? "font-bold underline text-black"
                : "font-medium text-[#222]"
            }`}
          >
            <button
              onClick={handleToggleMarkForReview}
              className="flex items-center text-sm cursor-pointer"
            >
              <BookmarkIcon
                className={`w-4 h-4 mr-1 ${
                  markForReview ? "text-red-600" : "text-gray-400"
                }`}
                stroke="black"
                strokeWidth={2.5}
              />
              Mark for Review
            </button>
          </div>
        </div>
        <div className="flex-1" />
        {/* Exclude Mode 토글 버튼 */}
        {question.answer_type === "multiple_choice" && (
          <button
            type="button"
            aria-label="Toggle Exclude Mode"
            className={`w-9 h-7 flex items-center justify-center border-1 border-black rounded-sm transition-colors ml-2 cursor-pointer my-0.5
              ${
                excludeMode
                  ? "bg-[#3451d6] text-white"
                  : "bg-white text-black hover:bg-gray-200"
              }`}
            onClick={onToggleExcludeMode}
          >
            <img
              src="/ic_abc.png"
              alt="Exclude Mode"
              className={`w-5 h-5 object-contain transition-all filter ${
                excludeMode ? "invert brightness-200 contrast-200" : ""
              }`}
              style={{
                filter: !excludeMode ? "grayscale(0) opacity(1)" : undefined,
              }}
            />
          </button>
        )}
      </div>
      <div
        className="w-full max-w-[650px] mx-auto border-b-2 border-black border-dashed"
        style={{ marginBottom: 24 }}
      />

      {/* 문제 텍스트 */}
      <div className="mb-6 max-w-[650px] mx-auto text-left w-full">
        <HighlightableContent
          content={question?.question || ""}
          contentType="question"
          onSaveHighlight={onSaveHighlight}
        />
      </div>

      {/* 선택지 */}
      {question.answer_type === "short_answer" ? (
        <div className="flex flex-col items-start max-w-[650px] mx-auto w-full mb-8">
          <input
            type="text"
            value={shortAnswer}
            onChange={(e) => {
              const value = e.target.value;
              setShortAnswer(value);
              saveAnswer({
                selected_choice_id: value,
                excluded_choices: [],
              });
              if (debounceTimeout.current)
                clearTimeout(debounceTimeout.current);
              debounceTimeout.current = setTimeout(() => {
                saveAnswer({
                  selected_choice_id: value,
                  excluded_choices: [],
                });
              }, 800);
            }}
            className="border rounded p-2 text-lg w-32 text-center mb-6"
            inputMode="text"
            pattern="[0-9./]*"
            placeholder="Enter answer"
          />
          <div className="mt-6 text-xl font-bold">
            Answer Preview:{" "}
            <span
              dangerouslySetInnerHTML={{
                __html: convertToFraction(shortAnswer),
              }}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3 max-w-[650px] mx-auto w-full">
          {question?.choices?.map((choice, idx) => {
            const isSelected = selectedChoice === choice.id;
            const isExcluded = excludedChoices.includes(choice.id);
            return (
              <div key={choice.id} className="flex items-stretch w-full">
                <label
                  className={`flex items-center px-0 py-0 cursor-pointer transition-all font-sans rounded-lg border pl-4 flex-1
                    ${
                      isSelected
                        ? "border-blue-700 bg-[#f5f8ff] shadow-[0_0_0_2px_#2563eb]"
                        : "border-[#d1d5db] bg-white hover:bg-[#f3f4f6]"
                    }
                    min-h-[56px] relative group
                    ${isExcluded ? "opacity-60" : ""}`}
                  style={{ padding: 0 }}
                  onClick={() => handleSelectChoice(choice.id)}
                >
                  {/* 동그라미 알파벳 */}
                  <span
                    className={`flex items-center justify-center w-6 h-6 rounded-full mr-4 ml-4 mt-0.5 text-base font-semibold border-black text-[14px]
                      ${
                        isSelected
                          ? "bg-blue-700 text-white border-1 border-black"
                          : "bg-white text-black border-1 border-[#bfc8e6]"
                      }
                      transition-all`}
                    style={{ minWidth: 28, minHeight: 28 }}
                  >
                    {getChoiceLetter(idx)}
                  </span>
                  {/* 선택지 텍스트 */}
                  <div className="flex-1 min-w-0 flex items-center h-full w-full">
                    <div
                      className="flex-1 font-normal w-full h-full text-[18px] text-gray-900 leading-relaxed font-serif font-times flex items-center"
                      style={{ fontFamily: '"Times New Roman", Times, serif' }}
                      dangerouslySetInnerHTML={{ __html: choice.text }}
                    />
                  </div>
                  {/* 제외 밑줄: label 전체를 가로지름 */}
                  {isExcluded && (
                    <span
                      className="absolute left-0 right-0 top-1/2 h-[2px] bg-black"
                      style={{ transform: "translateY(-50%)", zIndex: 2 }}
                    />
                  )}
                </label>
                {/* Exclude/Undo 버튼/아이콘: excludeMode가 true일 때만 노출 (선택지 테두리 밖) */}
                {excludeMode &&
                  (isExcluded ? (
                    <button
                      type="button"
                      className="ml-2 text-xs font-bold text-black underline hover:no-underline self-center bg-transparent border-none p-0 cursor-pointer"
                      style={{ boxShadow: "none" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExcludeChoice(choice.id);
                      }}
                    >
                      Undo
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="ml-4 flex items-center justify-center w-6 h-6 rounded-full text-[#3451d6] hover:bg-[#e6edf8] cursor-pointer transition-colors self-center"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleExcludeChoice(choice.id);
                      }}
                      aria-label="Exclude choice"
                    >
                      <span
                        className="relative flex items-center justify-center w-5 h-5 hover:bg-gray-200 rounded-full mr-4 ml-4 mt-0.5 text-base text-[12px] bg-white text-black border-1 border-gray-300 transition-all"
                        style={{ minWidth: 28, minHeight: 28 }}
                      >
                        {getChoiceLetter(idx)}
                        <span
                          className="absolute left-0 right-0 top-1/2 h-[2px] bg-black"
                          style={{ transform: "translateY(-50%)", zIndex: 2 }}
                        />
                      </span>
                    </button>
                  ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ExamQuestionPanel;
